import { chargeItemAction, imagingAction, labAction, procedureAction, ProductInventoryAction, roomAction } from '@api'
import { useHmoTarrif, useSearchableTabs } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { HmoTariffAttr } from '@type/hmo.type'
import { FormButton, FormInputField, Modal } from '@ui'
import { FormSelect, Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { HmoStatusOptions } from './AddHmo'

const schema = yup.object({
  tariffable_type: yup
    .string()
    .oneOf(['LabTest', 'ProductInventory', 'Procedure', 'Imaging', 'Room', 'ChargeItem'], 'Invalid tariffable type')
    .required('Tariffable type is required'),

  // percentage: yup
  //   .number()
  //   .typeError('Percentage must be a number')
  //   .min(-100, 'Percentage cannot be less than -100')
  //   .max(100, 'Percentage cannot be greater than 100')
  //   .required('Percentage is required'),

  tariffable_id: yup
    .number()
    .typeError('Tariffable ID is required')
    .integer('ID must be an integer')
    .positive('ID must be greater than 0')
    .required('Tariffable ID is required'),
  price: yup.string().required('Price is required'),
  status: yup.string().oneOf(['active', 'inactive'], 'Invalid status').required('Status is required'),
  code: yup.string().trim().nullable().notRequired()
})
type Props = {
  onClose: () => void
  id?: string
  defaultData?: HmoTariffAttr
  hmoId?: string
  planId?: string
}

const tariffableOptions = [
  { label: 'Lab test', value: 'LabTest' },
  { label: 'Medication', value: 'ProductInventory' },
  { label: 'Procedure', value: 'Procedure' },
  { label: 'Imaging', value: 'Imaging' },
  { label: 'Room', value: 'Room' },
  { label: 'ChargeItem', value: 'ChargeItem' }
]

export const AddTariff = ({ defaultData, onClose, id, hmoId, planId }: Props) => {
  const { activeTab } = useSearchableTabs({ defaultValue: 'ProductInventory' })
  const methods = useForm({
    resolver: yupResolver(schema),
    values: {
      ...defaultData,
      tariffable_id: defaultData?.tariffable_id,
      tariffable_type: defaultData?.tariffable_type || activeTab,
      price: defaultData?.price?.toString() || ''
    } as yup.InferType<typeof schema>
  })
  const { handleSubmit, formState, watch } = methods
  const tariffableType = watch('tariffable_type') || activeTab
  const { createTarrif } = useHmoTarrif()

  const loadOptionsMap: Record<string, any> = {
    ChargeItem: chargeItemAction,
    Imaging: imagingAction,
    LabTest: labAction,
    Procedure: procedureAction,
    ProductInventory: ProductInventoryAction,
    Room: roomAction
  }
  const onSubmit = async (formRecObj: yup.InferType<typeof schema>) => {
    const payload = {
      data: {
        type: 'hmo_tariffs',
        attributes: {
          ...formRecObj
        },
        relationships: {
          hmo: {
            data: {
              type: 'hmo',
              id: hmoId
            }
          },
          hmo_plan: {
            data: {
              type: 'hmo_plan',
              id: planId
            }
          }
        }
      }
    }

    try {
      await createTarrif(payload)
      showToast(`Tariff added successfully`)
      onClose()
    } catch (error) {
      getError(error)
    }
  }
  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Add Tariff">
        <form className="flex flex-col overflow-auto gap-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-4 overflow-auto p-6 pb-0 w-full min-w-0 max-w-[600px]">
            {!id && (
              <Controller
                control={methods.control}
                name="tariffable_type"
                render={({ field: { onChange, value } }) => (
                  <Select
                    getOptionValue={({ value }) => value}
                    label="Service Name"
                    onChange={(val: any) => onChange(val?.value)}
                    options={tariffableOptions}
                    value={tariffableOptions.find(({ value: optionValue }) => optionValue === value)}
                  />
                )}
              />
            )}
            <Controller
              control={methods.control}
              key={tariffableType}
              name="tariffable_id"
              render={({ field: { onChange }, fieldState: { error } }) => (
                <Select
                  borderRadius={'100px'}
                  defaultOptions
                  error={error?.message}
                  getOptionValue={({ value }) => value}
                  isAsync
                  label="Service"
                  loadOptions={loadOptionsMap[tariffableType]}
                  onChange={(val: any) => onChange(val?.value)}
                />
              )}
            />
            <div className="">
              <FormInputField label="Price" name="price" type="number" />
            </div>
            <FormSelect label="Status" name="status" options={HmoStatusOptions} />
          </div>
          <FormButton isLoading={formState.isSubmitting} onClose={onClose} submit="Save" />
        </form>
      </Modal>
    </FormProvider>
  )
}
